import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import axios from "axios";
import { LoadingButton } from "../LoadingButton.js";
import { getApiRoute } from "src/services";
import { isInvalidEmail } from "src/utils/Utils.js";

/**
 * Reusable feedback widget. Caller passes a `category` (e.g.
 * "signin-issues") and a list of `options` users can pick from.
 * Free-text always available. Submission is anonymous-safe — works
 * whether the user is signed in or not.
 *
 * Usage:
 *
 *   <FeedbackForm
 *     category="signin-issues"
 *     options={[
 *       { key: "email-never-arrived", label: "Email never arrived" },
 *       { key: "link-didnt-work", label: "Email arrived but the link didn't work" },
 *       { key: "in-spam", label: "It was in spam or Promotions" },
 *       { key: "other", label: "Something else" },
 *     ]}
 *     trigger={<Link>Having issues signing in?</Link>}
 *   />
 *
 * The `trigger` prop is whatever element opens the dialog. It receives
 * an onClick handler from this component.
 */
const FeedbackForm = ({
  category,
  options = [],
  trigger,
  title = "What happened?",
  submitLabel = "Send feedback",
  defaultEmail = "",
  initialFreeText = "",
  subjectLabel,
  initialSubject = "",
  onSubmitted,
  context = {},
}) => {
  const [open, setOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [subject, setSubject] = useState(initialSubject);
  const [freeText, setFreeText] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Re-seed if the parent's `defaultEmail` / `initialFreeText` arrive or
  // change while the form is closed (e.g. the SignIn email field, or the
  // request band's typed-in calendar name).
  useEffect(() => {
    if (!open) {
      setEmail(defaultEmail);
      setFreeText(initialFreeText);
      setSubject(initialSubject);
    }
  }, [defaultEmail, initialFreeText, initialSubject, open]);

  const reset = () => {
    setSelectedKeys(new Set());
    setSubject(initialSubject);
    setFreeText(initialFreeText);
    setEmail(defaultEmail);
    setIsSubmitting(false);
    setSubmitted(false);
    setError("");
  };

  const handleClose = () => {
    setOpen(false);
    // Reset on close so re-opening is a clean state.
    setTimeout(reset, 200);
  };

  const handleToggle = (key) => {
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedKeys(next);
  };

  const trimmedEmail = email.trim().toLowerCase();
  const emailIsValid = trimmedEmail === "" || !isInvalidEmail(trimmedEmail);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError("");
    try {
      await axios.post(
        getApiRoute("feedback", "POST_SUBMIT"),
        {
          category,
          optionsSelected: Array.from(selectedKeys),
          freeText:
            subjectLabel && subject.trim()
              ? freeText.trim()
                ? `${subject.trim()}\n\nDetails: ${freeText.trim()}`
                : subject.trim()
              : freeText.trim(),
          email: trimmedEmail,
          context,
        },
        { withCredentials: true }
      );
      setSubmitted(true);
      onSubmitted?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Couldn't send your feedback — please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    !isSubmitting &&
    emailIsValid &&
    (selectedKeys.size > 0 || freeText.trim().length > 0 || (subjectLabel && subject.trim().length > 0));

  return (
    <>
      <Box
        component="span"
        onClick={() => setOpen(true)}
        sx={{ cursor: "pointer", display: "inline-block" }}
      >
        {trigger}
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={handleClose}
            aria-label="close"
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {submitted ? (
          <DialogContent sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h2" sx={{ mb: 1 }}>
              Thanks for the report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We'll take a look. If we need more info, we'll reach out.
            </Typography>
            <Button variant="contained" onClick={handleClose}>
              Got it
            </Button>
          </DialogContent>
        ) : (
          <>
            <DialogTitle sx={{ pr: 5 }}>{title}</DialogTitle>
            <DialogContent>
              {options.length > 0 && (
                <FormGroup sx={{ mb: 2 }}>
                  {options.map((opt) => (
                    <FormControlLabel
                      key={opt.key}
                      control={
                        <Checkbox
                          checked={selectedKeys.has(opt.key)}
                          onChange={() => handleToggle(opt.key)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">{opt.label}</Typography>
                      }
                    />
                  ))}
                </FormGroup>
              )}
              {subjectLabel && (
                <TextField
                  fullWidth
                  variant="standard"
                  label={subjectLabel}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value.slice(0, 320))}
                  sx={{ mb: 2.5 }}
                />
              )}
              <TextField
                multiline
                minRows={3}
                maxRows={8}
                fullWidth
                placeholder="Tell us more (optional)"
                value={freeText}
                onChange={(e) => setFreeText(e.target.value.slice(0, 5000))}
                inputProps={{ "data-testid": "feedback-freetext" }}
              />
              <TextField
                type="email"
                fullWidth
                variant="standard"
                label="Your email (so we can help)"
                value={email}
                onChange={(e) => setEmail(e.target.value.slice(0, 320))}
                error={email.length > 0 && !emailIsValid}
                helperText={
                  email.length > 0 && !emailIsValid
                    ? "Please enter a valid email"
                    : "We'll only email you about this issue. No marketing."
                }
                inputProps={{ "data-testid": "feedback-email" }}
                sx={{ mt: 2 }}
              />
              {error && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                  {error}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <LoadingButton
                variant="contained"
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={!canSubmit}
                sx={{ width: "100%" }}
                data-testid="feedback-submit"
              >
                {submitLabel}
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default FeedbackForm;
